import { lazy, Suspense } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import { useSelector } from 'react-redux';
// Layouts and route guards are small and needed on every navigation, so they
// stay in the main bundle; every page is code-split into its own chunk.
import PublicLayout from '../layouts/PublicLayout';
import DashboardLayout from '../layouts/DashboardLayout';
import ProtectedRoute from './ProtectedRoute';
import FeatureRoute from './FeatureRoute';
import BrandedLoader from '../components/BrandedLoader';

const KioskEntryPage = lazy(() => import('../pages/kiosk/KioskEntryPage'));
const SharedBillPage=lazy(()=>import('../pages/public/SharedBillPage'));
const CustomerOrdersPage = lazy(() => import('../pages/public/CustomerOrdersPage'));
const CustomerProfilePage = lazy(() => import('../pages/public/CustomerProfilePage'));
const CustomerTermsPage = lazy(() => import('../pages/public/CustomerTermsPage'));
const LandingPage = lazy(() => import('../pages/public/LandingPage'));
const BrandProgressPreview = lazy(() => import('../pages/public/BrandProgressPreview'));
const BrandSplashPreview = lazy(() => import('../pages/public/BrandSplashPreview'));
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const ResetOtpPage = lazy(() => import('../pages/auth/ResetOtpPage'));const AdminDashboard = lazy(() => import('../pages/admin/AdminDashboard'));
const AdminUsersPage = lazy(() => import('../pages/admin/AdminUsersPage'));
const AdminShopsPage = lazy(() => import('../pages/admin/AdminShopsPage'));
const AdminBarLicensesPage=lazy(()=>import('../pages/admin/AdminBarLicensesPage'));
const AdminAlcoholPage = lazy(() => import('../pages/admin/AdminAlcoholPage'));
const AdminCatalogPage = lazy(() => import('../pages/admin/AdminCatalogPage'));
const AdminActivityLogPage = lazy(() => import('../pages/admin/AdminActivityLogPage'));
const AdminSettingsPage = lazy(() => import('../pages/admin/AdminSettingsPage'));
const AdminFinancePage = lazy(() => import('../pages/admin/AdminFinancePage'));
const ShopFinancePage = lazy(() => import('../pages/shop/ShopFinancePage'));
const AdminFoodPlazasPage = lazy(() => import('../pages/admin/AdminFoodPlazasPage'));
const FoodPlazaDashboard = lazy(() => import('../pages/plaza/FoodPlazaDashboard'));
const FoodPlazaPublicPage = lazy(() => import('../pages/plaza/FoodPlazaPublicPage'));
const CashInHandPage = lazy(() => import('../pages/shop/CashInHandPage'));
const StockReportsPage = lazy(() => import('../pages/shop/StockReportsPage'));
const ShopReportsPage = lazy(() => import('../pages/shop/ShopReportsPage'));
const AdminPricingPage = lazy(() => import('../pages/admin/pricing/AdminPricingPage'));
const DashboardIndex = lazy(() => import('../pages/dashboard/DashboardIndex'));
const ShopOffersPage = lazy(() => import('../pages/shop/ShopOffersPage'));
const ShopMenuPage = lazy(() => import('../pages/shop/ShopMenuPage'));
const StaffQueueRoute = lazy(() => import('./StaffQueueRoute'));
const SelfServicePage = lazy(() => import('../pages/shop/SelfServicePage'));
const KitchenPage = lazy(() => import('../pages/shop/KitchenPage'));
const LiquorQueuePage = lazy(() => import('../pages/shop/LiquorQueuePage'));
const TablesPage = lazy(() => import('../pages/shop/TablesPage'));
const RoomsPage = lazy(() => import('../pages/shop/RoomsPage'));
const DepartmentsPage = lazy(() => import('../pages/shop/DepartmentsPage'));
const DeliveryPage = lazy(() => import('../pages/shop/DeliveryPage'));
const InvoiceDesignerPage = lazy(() => import('../pages/shop/InvoiceDesignerPage'));
const ShopSettingsPage = lazy(() => import('../pages/shop/ShopSettingsPage'));
const SignupPage = lazy(() => import('../pages/auth/SignupPage'));
const ShopRegisterPage = () => <SignupPage kind="SHOP" />;
const ShopDashboard = lazy(() => import('../pages/shop/ShopDashboard'));
const ShopProfilePage = lazy(() => import('../pages/shop/ShopProfilePage'));
const StaffAccountsPage = lazy(() => import('../pages/shop/StaffAccountsPage'));
const StaffTakeOrderPage = lazy(() => import('../pages/shop/StaffTakeOrderPage'));
const StockEntryPage = lazy(() => import('../pages/shop/StockEntryPage'));
const PurchaseStockPage = lazy(() => import('../pages/shop/PurchaseStockPage'));
const BookingDeliveryPage=lazy(()=>import('../pages/shop/BookingDeliveryPage'));
const BookingMenuSelectionPage = lazy(() => import('../pages/shop/BookingMenuSelectionPage'));
const RoomPreordersPage = lazy(() => import('../pages/shop/RoomPreordersPage'));
const StaffWorkspace = lazy(() => import('../pages/shop/StaffWorkspace'));
const ApproverOrderPage = lazy(() => import('../pages/shop/ApproverOrderPage'));
const VisitServicesPage = lazy(() => import('../pages/public/VisitServicesPage'));
const ShopPublicPage = lazy(() => import('../pages/public/ShopPublicPage'));
const HotelPublicPage = lazy(() => import('../pages/public/HotelPublicPage'));
const HospitalPublicPage = lazy(() => import('../pages/public/HospitalPublicPage'));
const QueueBoardPage = lazy(() => import('../pages/public/QueueBoardPage'));
const NotFoundPage = lazy(() => import('../pages/public/NotFoundPage'));
const SubscriptionTermsPage = lazy(() => import('../pages/public/SubscriptionTermsPage'));
const RefundPolicyPage = lazy(() => import('../pages/public/RefundPolicyPage'));
const CorporatePoliciesPage = lazy(() => import('../pages/public/CorporatePoliciesPage'));
// Dev utilities are lazy-loaded and registered only in dev builds so Vite can
// exclude them (and their routes) from production bundles entirely.
const EmailTestPage = import.meta.env.DEV ? lazy(() => import('../pages/dev/EmailTestPage')) : null;
const BackendConnectivityPage = import.meta.env.DEV ? lazy(() => import('../pages/dev/BackendConnectivityPage')) : null;

const RouteFallback = () => <BrandedLoader message="Loading experience..." />;

const DashboardSettingsRoute = () => {
  const role = useSelector((state) => state.auth.user?.role);

  if (role === 'ADMIN') {
    return <AdminSettingsPage />;
  }

  if (role === 'SHOP_OWNER' || role === 'SHOP_STAFF') {
    return <ShopSettingsPage />;
  }

  return <Navigate to="/dashboard" replace />;
};

const AppRoutes = () => (
  <Suspense fallback={<RouteFallback />}>
    <Routes>
    <Route path="visit/:shopId/:kind" element={<VisitServicesPage />} />
    <Route path="kiosk" element={<KioskEntryPage />} />
    <Route path="bill-share" element={<SharedBillPage/>}/>
    <Route path="hotel/:hotelId" element={<HotelPublicPage />} />
    <Route path=":shopSlug/board" element={<QueueBoardPage />} />
    <Route path=":shopSlug" element={<ShopPublicPage />} />
    <Route path="menu/:shopSlug" element={<ShopPublicPage />} />
    <Route path="plaza/:plazaSlug" element={<FoodPlazaPublicPage />} />
    <Route path="plaza/:plazaSlug/shop/:shopSlug" element={<ShopPublicPage />} />

    {/* Standalone full-screen auth screens (no landing header / footer / 3D background) */}
    <Route path="brand-progress" element={<BrandProgressPreview />} />
    <Route path="brand-splash" element={<BrandSplashPreview />} />
    <Route path="auth/login" element={<LoginPage />} />
    <Route path="customer/signup" element={<SignupPage />} />
    <Route path="customer/login" element={<Navigate to="/auth/login" replace />} />
    <Route path="auth/reset" element={<ResetOtpPage />} />    <Route path="shops/register" element={<ShopRegisterPage />} />

    <Route element={<PublicLayout />}>
      <Route path="hospital/:hospitalId" element={<HospitalPublicPage />} />
      <Route index element={<LandingPage />} />
      <Route path="customer/orders" element={<CustomerOrdersPage />} />
      <Route path="customer/profile" element={<CustomerProfilePage />} />
      <Route path="customer-terms" element={<CustomerTermsPage />} />
      <Route path="about" element={<Navigate to="/#about" replace />} />
      <Route path="pricing" element={<Navigate to="/#pricing" replace />} />
      <Route path="features" element={<Navigate to="/#features" replace />} />
      <Route path="subscription-terms" element={<SubscriptionTermsPage />} />
      <Route path="refund-policy" element={<RefundPolicyPage />} />
      <Route path="policies" element={<CorporatePoliciesPage />} />
      {import.meta.env.DEV && (
        <>
          <Route path="dev/email-test" element={<EmailTestPage />} />
          <Route path="dev/backend-health" element={<BackendConnectivityPage />} />
        </>
      )}
      <Route path="*" element={<NotFoundPage />} />
    </Route>

    <Route element={<ProtectedRoute />}>
      <Route path="/dashboard" element={<DashboardLayout />}>
        <Route index element={<DashboardIndex />} />

        <Route element={<ProtectedRoute roles={['ADMIN']} />}>
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="shops" element={<AdminShopsPage />} />
          <Route path="food-plazas" element={<AdminFoodPlazasPage />} />
          <Route path="catalog" element={<AdminCatalogPage />} />
          <Route path="alcoholic-drinks" element={<AdminAlcoholPage />} />
          <Route path="bar-licenses" element={<AdminBarLicensesPage />} />
          <Route path="finance" element={<AdminFinancePage />} />
          <Route path="pricing" element={<AdminPricingPage />} />
          <Route path="menus" element={<AdminCatalogPage />} />
          <Route path="activity" element={<AdminActivityLogPage />} />
          <Route path="reports" element={<AdminDashboard activeTab="reports" />} />
        </Route>

        <Route element={<ProtectedRoute roles={['SHOP_OWNER']} />}><Route path="offers" element={<ShopOffersPage />} /><Route path="booking-menu" element={<BookingMenuSelectionPage />} />
          <Route path="cash-report" element={<CashInHandPage />} />
          <Route path="shop-finance" element={<ShopFinancePage />} />
          <Route path="stock-reports" element={<StockReportsPage />} />
          <Route element={<FeatureRoute anyOf={['ANALYTICS']} />}>
            <Route path="shop-reports" element={<ShopReportsPage key="sales" reportKind="sales" />} />
            <Route path="shop-reports/feedback" element={<ShopReportsPage key="feedback" reportKind="feedback" />} />
          </Route>
          <Route path="staff" element={<StaffAccountsPage />} />
          <Route path="profile" element={<ShopProfilePage />} />
          <Route path="settings/invoice-design" element={<InvoiceDesignerPage />} />
        </Route>
        <Route element={<ProtectedRoute roles={['FOOD_PLAZA']} />}>
          <Route path="food-plaza" element={<FoodPlazaDashboard />} />
          <Route path="food-plaza/:panel" element={<FoodPlazaDashboard />} />
        </Route>

        <Route path="settings" element={<DashboardSettingsRoute />} />
        <Route element={<ProtectedRoute roles={['SHOP_OWNER', 'SHOP_STAFF', 'SHOP_OPERATOR']} />}>
          <Route path="pre-orders" element={<RoomPreordersPage key="booking-create" />} />
          <Route path="booking-delivery" element={<BookingDeliveryPage />} />
          <Route path="bookings" element={<RoomPreordersPage key="booking-view" view="report" />} />
          <Route path="purchase-stock" element={<PurchaseStockPage />} />
          <Route path="purchase-stock/entries/:kind/new" element={<StockEntryPage />} />
          <Route path="purchase-stock/drafts/:id" element={<StockEntryPage />} />
          <Route path="liquor" element={<LiquorQueuePage />} />
          <Route path="queue" element={<StaffQueueRoute />} />
        </Route>
        <Route element={<ProtectedRoute roles={['SHOP_OPERATOR']} />}>
          <Route path="workspace" element={<StaffWorkspace />} />
          <Route path="workspace/take-order" element={<StaffTakeOrderPage />} />
          <Route path="workspace/approval-order" element={<ApproverOrderPage />} />
        </Route>

        <Route element={<ProtectedRoute roles={['SHOP_OWNER', 'SHOP_STAFF']} />}>
          <Route element={<FeatureRoute anyOf={['DIGITAL_MENU']} />}>
            <Route path="menu" element={<ShopMenuPage />} />
          </Route>
          <Route element={<FeatureRoute anyOf={['SELF_SERVICE']} moduleFlags={['isSelfServiceEnabled']} />}>
            <Route path="self-service" element={<SelfServicePage />} />
          </Route>
          <Route element={<FeatureRoute anyOf={['DEPARTMENT_MODE']} moduleFlags={['hospitalModeEnabled']} />}>
            <Route path="departments" element={<DepartmentsPage />} />
          </Route>
        </Route>
        <Route element={<ProtectedRoute roles={['SHOP_OWNER', 'SHOP_STAFF', 'HOTEL_USER']} />}>
          <Route element={<FeatureRoute anyOf={['TABLE_SERVICE']} moduleFlags={['isTableServiceEnabled']} />}>
            <Route path="tables" element={<TablesPage />} />
          </Route>
          <Route element={<FeatureRoute anyOf={['ROOM_SERVICE']} moduleFlags={['hotelModeEnabled']} />}>
            <Route path="rooms" element={<RoomsPage />} />
          </Route>
        </Route>

        <Route element={<ProtectedRoute roles={['SHOP_OPERATOR']} />}><Route path="cash-in-hand" element={<CashInHandPage />} /></Route>
        <Route element={<ProtectedRoute roles={['SHOP_OWNER', 'SHOP_STAFF', 'CHEF', 'SHOP_OPERATOR']} />}>
          <Route element={<FeatureRoute anyOf={['ORDERING', 'SELF_SERVICE']} moduleFlags={['isOnlineOrderingEnabled', 'isSelfServiceEnabled', 'isTableServiceEnabled', 'hospitalModeEnabled', 'hotelModeEnabled']} />}>
            <Route path="kitchen" element={<KitchenPage />} />
          </Route>
        </Route>

        <Route element={<ProtectedRoute roles={['SHOP_OWNER', 'SHOP_STAFF', 'DELIVERY']} />}>
          <Route element={<FeatureRoute anyOf={['ORDERING']} moduleFlags={['isOnlineOrderingEnabled']} />}>
            <Route path="delivery" element={<DeliveryPage />} />
          </Route>
        </Route>
      </Route>
    </Route>
    <Route element={<ProtectedRoute roles={['SHOP_OWNER', 'SHOP_STAFF']} />}>
      <Route path="/shope-dashboard" element={<DashboardLayout />}>
        <Route index element={<ShopDashboard />} />
      </Route>
    </Route>
    </Routes>
  </Suspense>
);

export default AppRoutes;
